import { useWebContent } from '../lib/webPrices'
import { assetUrl } from '../lib/api'

const TAGS = ['Sportovní rybolov', 'Plážový volejbal', 'Tenis', 'Výčep a udírna']

/**
 * Hero — první dojem při otevření webu.
 *
 * „Wow" efekt je čistě CSS (transform/opacity), takže nebrzdí LCP:
 *  - fotka se pomalu dotahuje ze záběru 110 % → 100 % (Ken Burns, 16 s)
 *  - jeden světelný přejezd přes hladinu půl vteřiny po načtení
 *  - texty nabíhají postupně zdola s rozostřením → ostře
 *  - dole pulzuje scroll nápověda
 * Pro `prefers-reduced-motion` se všechny animace vypnou (motion-reduce).
 */
export function Hero() {
  const content = useWebContent()
  const subtitle = (content.hero_subtitle as string) ||
    'Místo, kde si užijete den plný sportu, relaxu, ubytování a skvělého občerstvení.'
  const tags = (content.hero_tags as string)
    ? (content.hero_tags as string).split('·').map((x) => x.trim()).filter(Boolean)
    : TAGS

  const reveal = (delayMs: number) =>
    ({ animationDelay: `${delayMs}ms` }) as React.CSSProperties

  // Pobytová tlačítka: po sjetí na lištu přepnout skupinu (ubytování / rybolov) a zaostřit příjezd
  const goSearch = (group: 'ubytovani' | 'rybolov') => () => {
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('sobacov:search-group', { detail: group }))
      ;(document.querySelector('#rezervace input[type=date]') as HTMLInputElement | null)?.focus()
    }, 450)
  }

  return (
    <section id="top" className="relative w-full overflow-hidden bg-[#0d1a13]">
      {/* v2: nižší hero podle návrhu — vyhledávací lišta a karty jsou vidět bez scrollu */}
      <div className="relative w-full" style={{ height: 'min(50svh, 470px)', minHeight: 420 }}>
        {/* Fotka — pomalý dojezd záběru */}
        {/* Ken Burns na obalovém divu (vlastní GPU vrstva); fotka uvnitř statická — transform přímo na <img>
            s object-fit se ve Firefoxu překresluje po snímcích a cuká (Robin 26. 10.) */}
        <div className="absolute inset-0 will-change-transform animate-hero-zoom motion-reduce:animate-none [backface-visibility:hidden]">
          <img
            src={assetUrl(content.hero_image as string) || '/img/hero_areal_dron.webp?v=2026-10-24'}
            alt="Letecký pohled na areál Sobáčov — rybník s ostrovy, chatky, penzion a kurty při západu slunce"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '30% 30%' }}   /* výš a doprava: penzion vedle textu, ne pod ním (Robin 24. 10.) */
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </div>

        {/* Světelný přejezd přes hladinu — jednou po načtení */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden motion-reduce:hidden" aria-hidden="true">
          <div
            className="absolute top-0 bottom-0 w-[38%] animate-hero-sweep"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.22) 50%, transparent)' }}
          />
        </div>

        {/* Overlays — tmavší zleva a zdola pro čitelnost, fotka zůstává živá */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(13,26,19,.22) 0%, rgba(13,26,19,.04) 34%, rgba(13,26,19,.66) 100%)' }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, rgba(13,26,19,.46) 0%, rgba(13,26,19,.10) 46%, transparent 72%)' }}
        />

        {/* Obsah — zarovnaný dolů vlevo, editorial, nabíhá postupně */}
        <div className="relative h-full max-w-6xl mx-auto px-5 md:px-8 flex flex-col justify-center pb-10 md:pb-12">
          {/* max-w-4xl: tři tlačítka (sport · ubytování · rybolov) se vejdou do jedné řady */}
          <div className="max-w-4xl">
            <p
              className="flex items-center gap-2.5 text-white/85 text-[11px] md:text-xs font-semibold tracking-[0.22em] uppercase mb-3 animate-hero-reveal motion-reduce:animate-none"
              style={reveal(150)}
            >
              <span className="inline-block w-[22px] h-[2px] bg-brand-500" />
              Fishing &amp; Sport Resort · CHKO Litovelské Pomoraví
            </p>

            <h1
              className="text-white font-extrabold leading-[1.0] text-3xl sm:text-4xl md:text-5xl lg:text-[60px] animate-hero-reveal motion-reduce:animate-none"
              style={{
                ...reveal(320),
                fontFamily: "'Manrope','Inter',sans-serif",
                letterSpacing: '-0.03em',
                textShadow: '0 2px 30px rgba(0,0,0,.35)',
                textWrap: 'balance',
              }}
            >
              Sportovní areál
              <br />
              <span
                style={{ fontFamily: "'Instrument Serif',Georgia,serif", fontStyle: 'italic', fontWeight: 400, letterSpacing: '-0.01em' }}
              >
                Sobáčov
              </span>
            </h1>

            <p
              className="mt-4 text-white/90 text-base md:text-lg font-medium leading-relaxed max-w-xl animate-hero-reveal motion-reduce:animate-none"
              style={{ ...reveal(520), textShadow: '0 1px 16px rgba(0,0,0,.4)' }}
            >
              {subtitle}
            </p>


            {/* Primární akce — červená sport, zelená ubytování (penzion + tiny house),
                modrá rybolov (chatky, bivak, karavan) — Robin 24. 10. Obě pobytové
                přepnou vyhledávací lištu na svou skupinu a zaostří datum příjezdu. */}
            <div
              className="mt-5 flex flex-wrap items-center gap-3 animate-hero-reveal motion-reduce:animate-none"
              style={reveal(880)}
            >
              <a href="#aktivity" className="btn-book shadow-[0_18px_40px_-14px_rgba(229,2,69,.6)]">
                Rezervovat sport <span aria-hidden="true">→</span>
              </a>
              <a
                href="#rezervace"
                onClick={goSearch('ubytovani')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3 text-[15px] font-semibold rounded-[10px] text-white bg-forest-700 hover:bg-forest-800 shadow-[0_18px_40px_-14px_rgba(20,80,45,.6)] transition-colors whitespace-nowrap"
              >
                Rezervovat ubytování <span aria-hidden="true">→</span>
              </a>
              <a
                href="#rezervace"
                onClick={goSearch('rybolov')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3 text-[15px] font-semibold rounded-[10px] text-white bg-steel-700 hover:bg-steel-800 shadow-[0_18px_40px_-14px_rgba(30,60,90,.6)] transition-colors whitespace-nowrap"
              >
                Rezervovat rybolov <span aria-hidden="true">→</span>
              </a>
            </div>
            <p
              className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-white/85 text-[14px] font-semibold animate-hero-reveal motion-reduce:animate-none"
              style={reveal(700)}
            >
              {tags.map((t, i) => (
                <span key={t} className="flex items-center gap-3">
                  {i > 0 && <span className="w-1 h-1 rounded-full bg-brand-400" />}
                  {t}
                </span>
              ))}
            </p>
          </div>
        </div>

        {/* Scroll nápověda */}
        <a
          href="#ubytovani"
          aria-label="Posunout dolů"
          className="absolute bottom-5 right-5 md:right-8 flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors animate-hero-reveal motion-reduce:animate-none"
          style={reveal(1100)}
        >
          <span className="text-[10px] uppercase tracking-[0.28em] font-bold [writing-mode:vertical-rl] rotate-180">
            Prozkoumat
          </span>
          <span className="block w-px h-10 bg-gradient-to-b from-white/70 to-transparent animate-hero-cue motion-reduce:animate-none" />
        </a>
      </div>
    </section>
  )
}
